import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface EmptyStateProps {
  /** Ícone grande no topo */
  icon?: ReactNode;
  /** Título principal */
  title: string;
  /** Descrição explicativa */
  description?: string;
  /** Ação primária (botão, link) */
  action?: ReactNode;
  /**
   * 'padrao' — página/lista inteira vazia (título grande, respiro largo).
   * 'compacto' — vazio DENTRO de um card ou bloco.
   *
   * M-5 (2026-07-31): a variante nasceu porque o vazio de um bloco da ficha do
   * cliente, com o título em 27px, ficava **maior que o nome do próprio bloco**
   * logo acima. Ausência de dado não pode gritar mais alto que a seção.
   */
  tamanho?: 'padrao' | 'compacto';
  className?: string;
}

/**
 * Empty state — quando uma lista/grade está vazia.
 *
 * Use sempre que renderizar uma lista sem itens, em vez de só mostrar nada.
 * Comunica claramente que NÃO HÁ DADOS (vs ainda carregando).
 *
 * <EmptyState
 *   title="Nenhum usuário cadastrado"
 *   description="Comece adicionando o primeiro usuário da plataforma."
 *   action={<Button>Adicionar usuário</Button>}
 * />
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  tamanho = 'padrao',
  className,
}: EmptyStateProps) {
  const compacto = tamanho === 'compacto';
  return (
    <div className={cn('text-center px-6', compacto ? 'py-5' : 'py-12', className)}>
      {icon && (
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-paper-warm dark:bg-paper-dark-warm text-ink-3 dark:text-ink-dark-3 mb-4">
          {icon}
        </div>
      )}
      <h3
        className={cn(
          'font-display text-ink-1 dark:text-ink-dark-1 tracking-tight mb-1',
          compacto ? 'text-base text-ink-2 dark:text-ink-dark-2' : 'text-2xl',
        )}
      >
        {title}
      </h3>
      {description && (
        <p className="text-sm text-ink-2 dark:text-ink-dark-2 max-w-md mx-auto leading-relaxed">
          {description}
        </p>
      )}
      {action && <div className={compacto ? 'mt-3' : 'mt-6'}>{action}</div>}
    </div>
  );
}
